import { FunctionComponent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, MenuItem, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import LabelField from 'components/UI/LabelField';
import { faChevronDown, faChevronUp, faClock } from '@fortawesome/pro-regular-svg-icons';
import { DurationSelect } from './DurationSelect';
import PriceField from './PriceField';
import { PetServiceWrapper } from 'model/NewMultipleBooking';
import useNonInitialEffect from '@versiondos/hooks';
import Service from 'model/Service';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { usePermissions } from 'hooks';
import lockStyles from '../UI/priceFieldInteractionLock.module.scss';
import SaveCustomPriceCheckbox from '../UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from '../UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from '../UI/useSavedCustomPriceChoice';

interface DurationService {
    id: number;
    name: string;
}

export interface SelectServicesProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: PetServiceWrapper;
    placeholder?: string;
    options: Array<PetServiceWrapper>;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    information?: string;
    customizable?: boolean;
    isEvaluation?: boolean;
    petId?: number;
    onSelect: (option: PetServiceWrapper, data: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.down('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            paddingLeft: 25,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            maxHeight: 200,
            paddingTop: 13,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        placeholder: {
            color: '#7A7A7A',
            fontSize: 16,
            lineHeight: '27px'
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 28,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            alignItems: 'center',
            paddingRight: 10,
            width: '85%'
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'height': 45,
            '&:hover': {
                background: '#F8F8F8'
            },
            'cursor': 'pointer',
            'borderLeft': '6px solid #ffffff'
        },
        itemContent: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 20,
            paddingRight: 20
        },
        item: {
            fontSize: 14,
            lineHeight: '14px',
            [theme.breakpoints.up('md')]: {
                fontSize: 16,
                lineHeight: '16px'
            }
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        chevron: {
            fontSize: 16
        },
        bold: {
            fontWeight: 500
        },
        spacing: {
            marginLeft: 4,
            [theme.breakpoints.up('md')]: {
                marginLeft: 5
            }
        },
        sectionService: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            alignItems: 'center',
            marginRight: 4,
            height: 17
        },
        sectionDuration: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            [theme.breakpoints.down(400)]: {
                width: '50%'
            },
            [theme.breakpoints.between(400, 'md')]: {
                width: '43%'
            },
            [theme.breakpoints.up('md')]: {
                width: '35%'
            }
        },
        durationContainer: {
            display: 'flex',
            alignItems: 'center',
            height: 45
        },
        serviceItemContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        serviceNameContainer: {
            [theme.breakpoints.down(400)]: {
                width: '45%'
            },
            [theme.breakpoints.between(400, 'md')]: {
                width: '52%'
            },
            [theme.breakpoints.up('md')]: {
                width: '60%'
            }
        },
        customData: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 35
        },
        customSection: {
            width: '100%'
        },
        durationAndPrice: {
            'width': '100%',
            'paddingRight': 10,
            '& fieldset': {
                borderColor: '#D3D3D3 !important'
            },
            '& .MuiOutlinedInput-root': {
                height: '54px'
            },
            '& .MuiFormControl-root': {
                height: '54px'
            }
        },
        field: {
            '& .MuiOutlinedInput-adornedStart': {
                paddingLeft: '0px !important'
            }
        },
        color: {
            width: 12,
            height: 12,
            borderRadius: '100%',
            marginRight: 10
        },
        evaluationServiceContainer: {
            alignItems: 'center',
            display: 'flex'
        },
        evaluationServiceName: {
            display: 'flex',
            alignItems: 'center',
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        }
    })
);

export const SelectServices: FunctionComponent<SelectServicesProps> = props => {
    const classes = useStyles(props);
    const fieldClasses = useFieldStyles();
    const permissions = usePermissions();
    const canEditPrices = permissions.checkout.editPrices;
    const catalogFromOptions = Number(
        props.options.find(option => option.service.id === props.selectedOption?.service?.id)?.beforePrice
    );
    const catalogFromSelected = Number(props.selectedOption?.beforePrice);
    const catalogPrice =
        Number.isFinite(catalogFromOptions) && catalogFromOptions > 0
            ? catalogFromOptions
            : Number.isFinite(catalogFromSelected) && catalogFromSelected > 0
            ? catalogFromSelected
            : 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId: props.petId,
        serviceId: props.selectedOption?.service?.id,
        catalogPrice,
        currentPrice: Number(props.selectedOption?.price) || 0,
        useSavedCustomPrice: props.selectedOption?.useSavedCustomPrice
    });

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [serviceDuration, setServiceDuration] = useState<DurationService | undefined>(undefined);
    const [defaultState, setDefault] = useState<string | undefined>(undefined);
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);

    const canToggle = !props.isEvaluation;

    const changeHandler = (idSelected: number) => {
        const optionSelected = props.options.find(option => option.service.id === idSelected)!;
        setServiceDuration({
            id: optionSelected.durationInMinutes,
            name: `${
                Math.floor(optionSelected.durationInMinutes / 60) !== 0
                    ? Math.floor(optionSelected.durationInMinutes / 60) + ' h'
                    : ''
            } ${optionSelected.durationInMinutes % 60 !== 0 ? (optionSelected.durationInMinutes % 60) + ' min' : ''}`
        });
        props.onSelect(optionSelected, false);
        setDefault(optionSelected?.price?.toFixed(2));
    };

    useEffect(() => {
        if (props.selectedOption) {
            setServiceDuration({
                id: props.selectedOption.durationInMinutes,
                name: `${
                    Math.floor(props.selectedOption.durationInMinutes / 60) !== 0
                        ? Math.floor(props.selectedOption.durationInMinutes / 60) + ' h'
                        : ''
                } ${
                    props.selectedOption.durationInMinutes % 60 !== 0
                        ? (props.selectedOption.durationInMinutes % 60) + ' min'
                        : ''
                }`
            });
            setDefault(props.selectedOption?.price?.toFixed(2));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultState) {
                setDefault(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [defaultState]);

    const header = (
        <Box className={classes.headerContainer}>
            <Box className={classes.textContainer}>
                <Typography
                    className={clsx(classes.header, {
                        [classes.placeholder]: !props.selectedOption
                    })}
                >
                    {props.selectedOption?.service && (
                        <Box
                            className={classes.color}
                            style={{
                                backgroundColor:
                                    services.find((serv: Service) => serv.id === props.selectedOption?.service.id)
                                        ?.color ?? 'inherit'
                            }}
                        />
                    )}
                    {props.selectedOption?.service ? props.selectedOption?.service.name : props.placeholder}
                </Typography>
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed || props.isEvaluation ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const headerEvaluation = (
        <Box className={classes.evaluationServiceContainer}>
            <Box
                className={classes.color}
                style={{
                    backgroundColor: props.selectedOption?.service
                        ? services.find((s: Service) => s.id === props.selectedOption?.service.id)?.color ?? '#E0A34A'
                        : '#E0A34A'
                }}
            />
            <Typography className={classes.evaluationServiceName}>
                {props.selectedOption?.service?.name || props.placeholder}
            </Typography>
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            {props.options.map((opt, index) => (
                <Box
                    className={classes.itemContainer}
                    key={opt.service.id}
                    style={{
                        borderColor: services.find((serv: Service) => serv.id === opt.service.id)?.color ?? '#ffffff'
                    }}
                    id={`booking_option_service_${index}`}
                    onClick={() => {
                        if (!props.isEvaluation) changeHandler(opt.service.id);
                    }}
                >
                    <Box className={classes.itemContent}>
                        <Box className={clsx(classes.serviceItemContainer, classes.serviceNameContainer)}>
                            <Typography className={clsx(classes.item, classes.sectionService)}>
                                {opt.service.name}
                            </Typography>
                        </Box>
                        <Box className={classes.sectionDuration}>
                            <Box className={classes.serviceItemContainer}>
                                <Box className={classes.durationContainer}>
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        className={clsx(classes.item)}
                                    />
                                    <Typography className={clsx(classes.item, classes.spacing)}>
                                        {`${opt.duration?.hours ? opt.duration.hours + ' h ' : ''} ${
                                            opt.duration?.minutes ? opt.duration.minutes + ' min ' : ''
                                        }`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={classes.serviceItemContainer}>
                                <Typography className={clsx(classes.item, classes.bold, classes.spacing)}>
                                    ${opt.price?.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );

    const collapsed = (
        <Box
            className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}
            onClick={() => {
                if (canToggle) setIsCollapsed(false);
            }}
        >
            {header}
        </Box>
    );

    const expanded = (
        <Box
            className={clsx(classes.border, classes.expandedContainer)}
            onClick={() => {
                if (canToggle) setIsCollapsed(true);
            }}
        >
            {header}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => canToggle && setIsCollapsed(true)}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <LabelField {...props} />
                {props.isEvaluation ? (
                    <>{headerEvaluation}</>
                ) : (
                    <Box
                        className={classes.dropdownContainer}
                        id="booking_list_services"
                    >
                        {isCollapsed ? collapsed : expanded}
                    </Box>
                )}
                {props.customizable && props.selectedOption && (
                    <Box className={classes.customSection}>
                        <Box className={classes.customData}>
                            <DurationSelect
                                label="Duration"
                                selectedOption={props.selectedOption}
                                durationToAdd={serviceDuration}
                                type="pet"
                                onSelect={value => props.onSelect(value as PetServiceWrapper, false)}
                                className={classes.durationAndPrice}
                            />
                            <div
                                className={clsx({
                                    [lockStyles.interactionLock]: !canEditPrices
                                })}
                            >
                                <PriceField
                                    id="booking_service_input_price"
                                    label="Price"
                                    default={defaultState}
                                    selectedOption={props.selectedOption}
                                    onChange={props.onSelect}
                                    placeholder="$0.00"
                                    className={classes.field}
                                />
                            </div>
                        </Box>
                        {savedCustomPrice.show && (
                            <SavedCustomPriceChoice
                                petName={savedCustomPrice.petName}
                                savedPrice={savedCustomPrice.savedPrice}
                                mode={savedCustomPrice.mode}
                                disabled={!canEditPrices}
                                onChange={mode => {
                                    const next = savedCustomPrice.applyMode(mode);
                                    setDefault(next.price.toFixed(2));
                                    props.onSelect(
                                        {
                                            ...props.selectedOption!,
                                            price: next.price,
                                            beforePrice: catalogPrice || props.selectedOption!.beforePrice,
                                            useSavedCustomPrice: next.useSavedCustomPrice,
                                            customPriceId: next.customPriceId,
                                            customPriceAmount: next.price
                                        },
                                        true
                                    );
                                }}
                            />
                        )}
                        <SaveCustomPriceCheckbox
                            checked={!!props.selectedOption.saveCustomPrice}
                            disabled={!canEditPrices}
                            currentPrice={props.selectedOption.price}
                            defaultPrice={catalogPrice}
                            onChange={checked =>
                                props.onSelect(
                                    {
                                        ...props.selectedOption!,
                                        saveCustomPrice: checked,
                                        customPriceAmount: props.selectedOption!.price
                                    },
                                    true
                                )
                            }
                        />
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default SelectServices;
